import { Either, ReadonlyArray } from "@morphism/fp";
import React from "react";
import styled from "styled-components";

import { Button } from "~/ButtonV2";
import { DVIR as GenericDVIR, DVIRs as GenericDVIRs } from "~/DVIR";
import * as ErrorMessage from "~/ErrorMessage";
import { Graph } from "~/Graph";
import * as Layout from "~/Layout";
import * as Loading from "~/Loading";
import * as Log from "~/Log";
import { NotApplicable } from "~/NotApplicable";

import { Time } from "~/Time";
import * as Typography from "~/Typography";

export const DVIR = ({ dvir }: { readonly dvir: GenericDVIR }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  return (
    <>
      {isOpen && <GenericDVIR onClose={() => setIsOpen(false)} dvir={dvir} />}
      <Button onClick={() => setIsOpen(true)}>
        <Typography.Component strong>
          <GenericDVIR.Type {...dvir} /> DVIR {GenericDVIR.humanID(dvir)}
        </Typography.Component>
        <Layout.Inline.Component left>
          <GenericDVIRs.Table.Status dvir={dvir} />
        </Layout.Inline.Component>
      </Button>
    </>
  );
};

export function DVIRs(route: Log.Route) {
  const { driver, dateTime } = route;
  const {
    dvirs,
    loading,
    error,
    graphError,
  } = GenericDVIRs.Query.Driver.Query.use({
    ...driver,
    ...Time.DateTime.toDayInterval({ dateTime }),
  });

  // Display any non fatal errors and ant design's version of a toast message.
  React.useEffect(() => {
    if (graphError) {
      Graph.Errors.displayMessage(graphError);
    }
  }, [graphError]);

  return (
    <Layout.Section.Component>
      <Layout.Section.Header.Component
        Left={() => (
          <Layout.Section.Header.Title>DVIRs</Layout.Section.Header.Title>
        )}
      />
      {loading ? (
        <Layout.Block.Component all>
          <Loading.Component />
        </Layout.Block.Component>
      ) : error ? (
        <Layout.Block.Component all>
          <ErrorMessage.Component icon danger />
        </Layout.Block.Component>
      ) : (
        pipe(
          dvirs,
          ReadonlyArray.map((dvir) => <DVIR key={dvir.id} dvir={dvir} />),
          ([first, ...rest]) => (
            <DVIRs.Scroll>
              <Layout.Block.Component all>
                {first ? (
                  <Layout.Space>{[first, ...rest]}</Layout.Space>
                ) : (
                  <NotApplicable>None</NotApplicable>
                )}
              </Layout.Block.Component>
            </DVIRs.Scroll>
          )
        )
      )}
    </Layout.Section.Component>
  );
}

export namespace DVIRs {
  export const fromRoute = (route: Log.Route) => (
    dvirs: GenericDVIRs
  ): GenericDVIRs =>
    pipe(
      dvirs,
      ReadonlyArray.filter((dvir) =>
        pipe(
          dvir.driver,
          Either.fold(
            () => false,
            ({ id }) =>
              id === route.driver.id &&
              Time.DateTime.toStartOfDay(route).dateTime ===
                Time.DateTime.toStartOfDay(dvir.lifecycle.submitted).dateTime
          )
        )
      )
    );

  export const Scroll = styled.div`
    overflow-x: scroll;
  `;
}
